import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Gift, Sprout, Music4, HeartPulse, UsersRound } from "lucide-react";
import { HERO } from "../../data/homeContent";

// Gary's world assembling around Resonance Intelligence. cx/cy are SVG coords (0–100)
// for the connective lines; top/left position the DOM nodes.
// The five Resonance Intelligence categories orbiting the central concept (1.1).
// Per the 2608 brief the stock photography here is replaced by a unified family of
// symbols — one library, one grid, one stroke weight — so the centre stays the focal point.
const WORLDS = [
  { label: "Presence", Icon: Sprout, top: "1%", left: "50%", cx: 56, cy: 8 },
  { label: "Musical Catalysts", Icon: Music4, top: "26%", left: "85%", cx: 90, cy: 32 },
  { label: "Leadership", Icon: Compass, top: "66%", left: "80%", cx: 86, cy: 74 },
  { label: "Gatherings", Icon: UsersRound, top: "78%", left: "18%", cx: 22, cy: 84 },
  { label: "Healthcare", Icon: HeartPulse, top: "22%", left: "1%", cx: 8, cy: 27 },
];

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 26 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] },
});

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = ({ onFind }) => {
  const reduce = useReducedMotion();
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28 grain">
      <div className="aurora-weave" />
      <div className="line-weave" />
      <div className="absolute inset-0 hex-texture opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[60rem] h-[60rem] radial-glow blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
        <div>
          <motion.h1 {...fade(0.14)} className="font-display font-bold leading-[1.03] text-4xl sm:text-5xl lg:text-[4.4rem] text-cream">
            Advancing the <span className="text-gold">Art of Human Being</span>
          </motion.h1>

          <motion.p {...fade(0.26)} className="mt-7 text-lg md:text-xl text-cream/75 leading-relaxed max-w-xl">
            {HERO.subhead}
          </motion.p>

          <motion.div {...fade(0.36)} className="mt-9 flex flex-col sm:flex-row gap-4">
            <button data-testid="hero-explore-cta" onClick={() => scrollTo("#ways-to-enter")} className="btn-gold px-8 py-4 rounded-full text-base font-semibold tracking-wide flex items-center justify-center gap-2">
              {HERO.primaryCta} <ArrowRight size={18} />
            </button>
            <button data-testid="hero-path-cta" onClick={() => onFind?.()} className="btn-ghost-gold px-8 py-4 rounded-full text-base font-medium tracking-wide flex items-center justify-center gap-2">
              <Compass size={18} className="text-[#E7CC86]" /> {HERO.secondaryCta}
            </button>
          </motion.div>

          <motion.div {...fade(0.42)} className="mt-5">
            <Link to="/gift" data-testid="hero-gift-link" className="inline-flex items-center gap-2 text-[#E7CC86] text-sm font-semibold hover:gap-3 transition-all">
              <Gift size={16} /> Experience a Gift of Resonance <ArrowRight size={14} />
            </Link>
          </motion.div>

          <motion.div {...fade(0.5)} className="mt-8 flex items-center gap-3">
            <span className="h-px w-8 bg-[#C9A24B]/50" />
            <p className="text-sm text-cream/55 max-w-md">{HERO.founder}</p>
          </motion.div>
        </div>

        <div className="relative hidden lg:block">
          <motion.div
            data-testid="hero-resonance-visual"
            initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-square max-w-[40rem] mx-auto"
          >
            <div className="absolute inset-0 radial-glow-strong blur-2xl" />
            <div className="absolute inset-[8%] glow-bronze blur-3xl opacity-70" />

            {/* connective resonance lines from center to each world */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" fill="none">
              {WORLDS.map((w, i) => (
                <motion.line key={w.label} x1="50" y1="50" x2={w.cx} y2={w.cy}
                  stroke="#C9A24B" strokeOpacity="0.3" strokeWidth="0.4" vectorEffect="non-scaling-stroke"
                  initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ delay: 0.9 + i * 0.16, duration: 0.8 }} />
              ))}
            </svg>

            {/* base concentric rings */}
            {[0, 1, 2, 3].map((r) => (
              <div key={r} className="absolute rounded-full border border-[#C9A24B]/18" style={{ inset: `${9 + r * 10}%` }} />
            ))}

            {/* expanding pulse waves — the "rhythm" */}
            {[0, 1, 2].map((p) => (
              <motion.div key={p} className="absolute rounded-full border border-[#E7CC86]/40" style={{ inset: "37%" }}
                animate={reduce ? {} : { scale: [1, 2.5], opacity: [0.55, 0] }}
                transition={{ duration: 4.2, repeat: Infinity, delay: p * 1.4, ease: "easeOut" }} />
            ))}

            {/* glowing core */}
            <div className="absolute inset-[29%] rounded-full bg-gradient-to-br from-[#E7CC86]/22 to-transparent blur-lg animate-pulse-glow" />

            {/* central concept */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-10 z-20">
              <p className="font-display font-semibold text-xl md:text-[1.7rem] leading-[1.04] tracking-[0.16em] text-gold">RESONANCE</p>
              <p className="font-display font-semibold text-xl md:text-[1.7rem] leading-[1.04] tracking-[0.16em] text-gold">INTELLIGENCE</p>
              <p className="mt-2.5 font-serif italic text-sm text-cream/70">Deepening Our Humanity in the Age of AI</p>
            </div>

            {/* Gary's world assembling around the concept */}
            {WORLDS.map((w, i) => (
              <motion.div key={w.label} className="absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{ top: w.top, left: w.left }}
                initial={{ opacity: 0, scale: 0.8, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 1 + i * 0.18, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
                <motion.div animate={reduce ? {} : { y: [0, -9, 0] }} transition={{ duration: 5 + i, repeat: Infinity, ease: "easeInOut" }}
                  className="flex flex-col items-center gap-2.5 w-28">
                  <span className="grid place-items-center w-16 h-16 lg:w-[4.5rem] lg:h-[4.5rem] rounded-2xl bg-[#0B1020]/85 backdrop-blur-sm ring-1 ring-[#C9A24B]/40 shadow-[0_18px_44px_-16px_rgba(0,0,0,0.9)]">
                    <w.Icon size={28} strokeWidth={1.5} className="text-[#E7CC86]" aria-hidden="true" />
                  </span>
                  <span className="text-center text-[11px] tracking-[0.14em] uppercase text-cream/85 leading-tight">{w.label}</span>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
